import React, { useState } from 'react';
import moment from 'moment';
import AppointmentDateRange from 'components/AppointmentDateRange/AppointmentDateRange';
import ClinicFilter from 'pages/AdminAppointmentPage/ClinicFilter/ClinicFilter';
import Table from './Table/Table';

import {
  CALENDAR_TYPE,
  getDateRangeByCalendarType,
} from 'utils/getDateRangeByCalendarType';
import axiosInstance from 'apis/axiosInstance';
import DoughnutChart from './DoughnutChart/DoughnutChart';

const defaultTableSortState = {
  sortBy: 'appointmentDate',
  sort: 'desc' as const,
};

const AdminPracticeInsightPage = () => {
  const [selectedClinicIds, setSelectedClinicIds] = useState<string[]>([]);

  const [cancelReasonCode, setCancelReasonCode] = useState<string | undefined>(
    undefined
  );

  const [shouldRun, setShouldRun] = useState(true);

  const [selectedCalendarType, setSelectedCalendarType] = useState(
    CALENDAR_TYPE.THIS_MONTH
  );

  const [sort, setSort] = useState<'asc' | 'desc'>(defaultTableSortState.sort);

  const [sortBy, setSortBy] = useState(defaultTableSortState.sortBy);

  const [selectedDateRange, setSelectedDateRange] = useState<{
    fromDate: string;
    toDate: string;
  }>(
    () =>
      getDateRangeByCalendarType(CALENDAR_TYPE.THIS_MONTH) as {
        fromDate: string;
        toDate: string;
      }
  );

  const onChangeDateRange = (dateRange: {
    fromDate: string;
    toDate: string;
  }) => {
    setSelectedDateRange(dateRange);
  };

  const onChangeClinic = async (clinicIds: string[]) => {
    setShouldRun(false);
    if (selectedCalendarType === CALENDAR_TYPE.ALL_TIME) {
      try {
        const rs = await axiosInstance.get('/appointments/date-range', {
          params: { clinicIds: clinicIds },
        });
        onChangeDateRange({
          fromDate: moment
            .utc(rs.data.earliestDateTime ?? selectedDateRange.fromDate)
            .format('YYYY-MM-DD'),
          toDate: moment
            .utc(rs.data.latestDateTime ?? selectedDateRange.toDate)
            .format('YYYY-MM-DD'),
        });
      } catch (error) {}
    }
    setSelectedClinicIds(clinicIds);
    setShouldRun(true);
  };

  return (
    <div
      className="pt-2.4 pl-1.8 pr-4 text-14 leading-[2.1rem] text-darkest-grey pb-12 relative"
      data-testid="page-admin-insight"
    >
      <div className="flex flex-row justify-between">
        <h3 className="text-24 font-bold leading-[3.6rem]">
          Practice Insights Report
        </h3>
      </div>

      <div className="my-2.4 flex gap-x-1.6 items-center">
        <ClinicFilter onSelect={onChangeClinic} />

        <div className="flex-1 text-right">
          <AppointmentDateRange
            selectedCalendarType={selectedCalendarType}
            setSelectedCalendarType={setSelectedCalendarType}
            clinicIds={selectedClinicIds}
            dateRange={selectedDateRange}
            onChangeDateRange={onChangeDateRange}
          />
        </div>
      </div>

      <div>
        <DoughnutChart
          clinicIds={selectedClinicIds}
          dateRange={selectedDateRange}
          shouldRun={shouldRun}
          onClickCancelReasonCode={(code) => setCancelReasonCode(code)}
          cancelReasonCode={cancelReasonCode}
        />
      </div>

      <div className="mt-5.6">
        <Table
          clinicIds={selectedClinicIds}
          dateRange={selectedDateRange}
          sort={sort}
          setSort={setSort}
          sortBy={sortBy}
          setSortBy={setSortBy}
          shouldRun={shouldRun}
          cancelReasonCode={cancelReasonCode}
        />
      </div>
    </div>
  );
};

export default AdminPracticeInsightPage;
